import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { SpaceBetween } from "@amzn/awsui-components-react";

import { RootState } from "../app/rootReducer";
import {
  ValidationActionButton,
  ValidationEditButtons,
} from "../porAsinList/TableCommonElements";
import { isNoFilterApplied } from "../searchSideBar/queryStringHelpers";
import { NoFiltersPanel } from "./NoFiltersPanel";
import { setValidationPageNumber } from "./asinValidationTableConnector";
import { StatusOverview } from "./statusOverview/StatusOverview";
import { fetchStepBreakdownAggregation } from "./stepBreakdown/stepBreakdownAggregationConnector";
import { ValidationTabs } from "./tabs/ValidationTabs";
import { ValidationModal } from "./validationAttributeModal";

/**
 * Renders the Status Overview above the tab strip when a filter is
 * applied. Otherwise shows a zero-state panel guiding the user to the
 * Property Filter.
 */
export const ValidationDashboardPage: React.FC = () => {
  const dispatch = useDispatch();
  const queryString = useSelector(
    (state: RootState) => state.generateQuery.queryString
  );
  // Flipped by save / submit thunks (e.g. bulk pass-exception
  // submission) to signal that dependent views should refetch. Watched
  // here so the step-breakdown pivot re-runs alongside the table when
  // the underlying data changes.
  const refreshFlipper = useSelector(
    (state: RootState) => state.asinValidationTableControl.refreshFlipper
  );
  const zeroState = isNoFilterApplied(queryString);

  // Fire the pivot aggregation on every filter change regardless of
  // the active tab so switching to the step-breakdown tab shows
  // results immediately. The thunk dedupes on `lastFetchedQueryString`.
  useEffect(() => {
    if (zeroState || queryString === undefined) {
      return;
    }
    dispatch(fetchStepBreakdownAggregation(queryString));
  }, [dispatch, queryString, zeroState, refreshFlipper]);

  if (zeroState) {
    return (
      <div data-testid="validation-dashboard-page">
        <NoFiltersPanel
          dataNoun="validation data"
          onResetPagination={() =>
            dispatch(setValidationPageNumber({ pageNumber: 1 }))
          }
        />
      </div>
    );
  }

  return (
    <div data-testid="validation-dashboard-page">
      <ValidationModal />
      <SpaceBetween size="l">
        <ValidationDashboardHeader />
        <StatusOverview />
        <ValidationTabs />
      </SpaceBetween>
    </div>
  );
};

const ValidationDashboardHeader: React.FC = () => {
  const totalCount = useSelector((state: RootState) => state.porAsinList.count);
  const isEditing = useSelector(
    (state: RootState) => state.asinValidationTableControl.isEditing
  );

  return (
    <div
      data-testid="validation-dashboard-run-validation"
      style={{ display: "flex", justifyContent: "flex-end" }}
    >
      {isEditing ? (
        <ValidationEditButtons />
      ) : (
        <ValidationActionButton totalCount={totalCount} />
      )}
    </div>
  );
};

export default ValidationDashboardPage;
